import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Calendar, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId, currentStatus) => {
    const newStatus = currentStatus === 'new' ? 'read' : 'new';
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/contact/${messageId}/status`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: newStatus },
      });
      toast.success(`Mensaje marcado como ${newStatus === 'read' ? 'leído' : 'nuevo'}`);
      fetchMessages();
    } catch (error) {
      console.error('Error updating message status:', error);
      toast.error('Error al actualizar estado');
    }
  };

  return (
    <div data-testid="admin-messages-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Mensajes</h1>
        <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Mensajes de Contacto</p>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-lg font-light text-ink/60">Cargando mensajes...</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="bg-paper p-12 editorial-border text-center">
          <p className="text-lg font-light text-ink/60">No hay mensajes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-paper editorial-border p-6 ${
                message.status === 'new' ? 'border-l-4 border-l-editorial' : ''
              }`}
              data-testid="message-item"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold tracking-tight mb-1">{message.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-ink/60">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {message.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(message.date).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handleMarkAsRead(message.id, message.status)}
                  variant={message.status === 'new' ? 'default' : 'outline'}
                  size="sm"
                  data-testid="mark-read-button"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {message.status === 'new' ? 'Marcar Leído' : 'Marcar Nuevo'}
                </Button>
              </div>
              <p className="text-base text-ink/80 leading-relaxed whitespace-pre-line">{message.message}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;